import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, Send, StickyNote, ArrowRightLeft, MessageSquare, History, User, Package } from "lucide-react";
import { LANG_META, STATUS_META, PRIORITY_META, SLA_META, metaLabel, fmtDuration } from "@/lib/supportUi";
import { useContent } from "@/context/ContentContext";
import { money } from "@/lib/api";
import { AttachmentPicker, AttachmentList, uploadTicketFiles } from "@/components/shared/SupportAttachments";
import { MsgTicks, newClientMsgId } from "@/components/support/MsgTicks";
import { useTr } from "@/lib/tr";

const inp = "w-full rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-sm text-white outline-none focus:ring-2 focus:ring-accent/40";

function Chip({ meta, tr }) {
  if (!meta) return null;
  return <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${meta.color}`}>{meta.flag ? meta.flag + " " : ""}{metaLabel(meta, tr)}</span>;
}

function OrderCard({ o, tr }) {
  if (!o) return null;
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3 text-xs" data-testid="ticket-order-card">
      <div className="mb-2 flex items-center gap-1.5 font-semibold text-slate-300"><Package className="h-3.5 w-3.5" /> {tr("td.linked_order", "Commande associée")}</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-slate-400">
        <div>{tr("td.number", "N°")} <span className="text-slate-200">#{o.order_number}</span></div>
        <div>{tr("td.status", "Statut")} <span className="text-slate-200">{o.status}</span></div>
        <div className="col-span-2">{tr("td.service", "Service")} <span className="text-slate-200">{o.service_name}</span></div>
        <div>{tr("td.qty", "Quantité")} <span className="text-slate-200">{o.quantity?.toLocaleString("en-US")}</span></div>
        <div>{tr("td.amount", "Montant")} <span className="text-slate-200">{money(o.amount)}</span></div>
        <div>{tr("td.progress", "Progression")} <span className="text-slate-200">{o.progress}%</span></div>
        <div>{tr("td.created", "Créée")} <span className="text-slate-200">{o.created_at ? new Date(o.created_at).toLocaleDateString("fr-FR") : "—"}</span></div>
      </div>
    </div>
  );
}

export function TicketDrawer({ id, base, isAdmin, agents = [], onChanged, onClose }) {
  const { t: tr } = useContent();
  const [t, setT] = useState(null);
  const [tab, setTab] = useState("conv");
  const [msg, setMsg] = useState("");
  const cmid = useRef(newClientMsgId());
  const tr2 = useTr();
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [macros, setMacros] = useState([]);
  const [showMacros, setShowMacros] = useState(false);
  const [staffTyping, setStaffTyping] = useState(false);
  const lastTyped = useRef(0);
  const [showTransfer, setShowTransfer] = useState(false);
  const [xfer, setXfer] = useState({ target: "admin", reason: "" });
  const [langPanel, setLangPanel] = useState(false);
  const [langXfer, setLangXfer] = useState({ target: "admin", reason: "" });
  const endRef = useRef(null);
  const path = `${base}/tickets/${id}`;

  const load = () => api.get(path).then((r) => setT(r.data)).catch(() => toast.error(tr("td.load_error", "Erreur de chargement")));
  useEffect(() => { load(); const iv = setInterval(load, 4000); return () => clearInterval(iv); }, [id]);
  useEffect(() => {
    const iv = setInterval(() => api.get(`${path}/typing`).then((r) => setStaffTyping(!!r.data.typing)).catch(() => {}), 3000);
    return () => clearInterval(iv);
  }, [id]);
  useEffect(() => {
    const lang = t?.language;
    const mp = isAdmin ? `${base}/macros` : `${base}/macros`;
    api.get(lang && ["fr", "en", "sw"].includes(lang) ? `${mp}?language=${lang}` : mp).then((r) => setMacros(r.data || [])).catch(() => {});
  }, [t?.language, base, isAdmin]);
  const onType = () => { const now = Date.now(); if (now - lastTyped.current > 2500) { lastTyped.current = now; api.post(`${path}/typing`, {}).catch(() => {}); } };
  const applyMacro = async (mid) => {
    try { const { data } = await api.post(`${path}/macros/${mid}/render`, {}); setMsg((prev) => (prev ? prev + "\n" : "") + (data.content || "")); setShowMacros(false); }
    catch { toast.error(tr("td.error", "Erreur")); }
  };
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [t?.messages?.length]);

  const canReply = isAdmin || t?.can_reply;
  const canManage = isAdmin || t?.can_manage;
  const canTransfer = isAdmin || t?.can_transfer;

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); onChanged && onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || tr("td.error", "Erreur")); }
    finally { setBusy(false); }
  };

  const reply = () => {
    if (!msg.trim() && !files.length) return;
    act(async () => {
      const { data } = await api.post(`${path}/reply`, { message: msg.trim() || "(pièce jointe)", client_msg_id: cmid.current });
      cmid.current = newClientMsgId();
      if (files.length) {
        try { await uploadTicketFiles(files, `${path}/attachments`, data.message_id); }
        catch { toast.error(tr("td.att_error", "Échec de l'envoi d'une pièce jointe")); }
      }
      setMsg(""); setFiles([]);
    });
  };  const addNote = () => { if (!note.trim()) return; act(async () => { await api.post(`${path}/note`, { note }); setNote(""); }, tr("td.note_added", "Note interne ajoutée")); };
  const setStatus = (status) => act(async () => { await api.post(`${path}/status`, { status }); }, tr("td.status_updated", "Statut mis à jour"));
  const setLang = (language) => act(async () => { await api.post(`${path}/language`, { language }); }, tr("td.lang_updated", "Langue mise à jour"));
  const setPriority = (priority) => act(async () => { await api.post(`${path}/priority`, { priority }); });
  const assign = (agent_id) => act(async () => { await api.post(`${path}/assign`, { agent_id: agent_id || null }); }, tr("td.assign_updated", "Assignation mise à jour"));
  const doTransfer = () => {
    if (!xfer.reason.trim()) return toast.error(tr("td.reason_required", "Le motif est obligatoire"));
    act(async () => {
      const body = xfer.target === "admin" ? { to_admin: true, reason: xfer.reason } : { to_agent_id: xfer.target, reason: xfer.reason };
      await api.post(`${path}/transfer`, body);
      setShowTransfer(false); setXfer({ target: "admin", reason: "" });
    }, tr("td.transferred", "Ticket transféré"));
  };
  const dismissLang = () => act(async () => { await api.post(`${path}/language-change/dismiss`, {}); }, tr("td.msg_validated", "Message validé dans la langue actuelle"));
  const confirmLang = () => {
    if (!langXfer.reason.trim()) return toast.error(tr("td.reason_required", "Le motif est obligatoire"));
    act(async () => {
      const body = langXfer.target === "admin" ? { to_admin: true, reason: langXfer.reason } : { to_agent_id: langXfer.target, reason: langXfer.reason };
      await api.post(`${path}/language-change/confirm`, body);
      setLangPanel(false); setLangXfer({ target: "admin", reason: "" });
    }, tr("td.lang_confirmed", "Changement de langue confirmé — ticket transféré"));
  };

  const langAgents = agents.filter((a) => !t || (t.language && ["fr", "en", "sw"].includes(t.language) ? (a.allowed_languages || []).includes(t.language) : true));
  const plc = t?.pending_language_change;
  const plcLang = plc?.language;
  const newLangAgents = plcLang ? agents.filter((a) => (a.allowed_languages || []).includes(plcLang)) : [];

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose}>
      <div className="flex h-full w-full max-w-xl flex-col bg-slate-900" onClick={(e) => e.stopPropagation()} data-testid="ticket-drawer">
        {!t ? <div className="flex flex-1 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div> : (
          <>
            {/* header */}
            <div className="border-b border-slate-800 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="truncate font-display font-bold text-white" data-testid="drawer-subject">{t.subject}</h2>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <Chip meta={LANG_META[t.language]} tr={tr} />
                    <Chip meta={STATUS_META[t.status]} tr={tr} />
                    <span className={`text-[11px] ${PRIORITY_META[t.priority]?.color}`}>● {metaLabel(PRIORITY_META[t.priority], tr)}</span>
                    {t.sla && <span data-testid="sla-badge" className={`rounded-full border px-2 py-0.5 text-[10px] ${SLA_META[t.sla.status]?.color || SLA_META.unknown.color}`}>{metaLabel(SLA_META[t.sla.status] || SLA_META.unknown, tr)}{(t.sla.remaining_minutes !== null && t.sla.remaining_minutes !== undefined) ? ` · ${fmtDuration(t.sla.remaining_minutes)}` : ""}</span>}
                  </div>
                </div>
                <button onClick={onClose} data-testid="drawer-close"><X className="h-5 w-5 text-slate-400" /></button>
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-400"><User className="h-3.5 w-3.5" /> {t.user_name} · {t.user_email}</div>
              {t.assigned_agent_name && <div className="mt-1 text-[11px] text-slate-500">{tr("td.assigned_to", "Assigné à :")} {t.assigned_agent_name}</div>}
            </div>

            {/* admin controls */}
            {isAdmin && (
              <div className="grid grid-cols-3 gap-2 border-b border-slate-800 bg-slate-950/40 p-3">
                <div>
                  <label className="text-[10px] uppercase text-slate-500">{tr("td.language", "Langue")}</label>
                  <select value={t.language} onChange={(e) => setLang(e.target.value)} data-testid="drawer-language" className={inp}>
                    {["fr", "en", "sw", "unverified"].map((l) => <option key={l} value={l}>{metaLabel(LANG_META[l], tr)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-500">{tr("td.priority", "Priorité")}</label>
                  <select value={t.priority} onChange={(e) => setPriority(e.target.value)} data-testid="drawer-priority" className={inp}>
                    {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{metaLabel(v, tr)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase text-slate-500">{tr("td.agent", "Agent")}</label>
                  <select value={t.assigned_agent_id || ""} onChange={(e) => assign(e.target.value)} data-testid="drawer-assign" className={inp}>
                    <option value="">{tr("td.unassigned", "Non assigné")}</option>
                    {langAgents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              </div>
            )}

            {/* tabs */}
            <div className="flex border-b border-slate-800 text-sm">
              {[["conv", tr("td.tab.conv", "Conversation"), MessageSquare], ["notes", tr("td.tab.notes", "Notes internes"), StickyNote], ["hist", tr("td.tab.hist", "Transferts"), History]].map(([k, label, Icon]) => (
                <button key={k} onClick={() => setTab(k)} data-testid={`drawer-tab-${k}`}
                  className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 ${tab === k ? "border-b-2 border-accent text-white" : "text-slate-400"}`}>
                  <Icon className="h-4 w-4" /> {label}
                </button>
              ))}
            </div>

            {/* language-change alert */}
            {plc && (
              <div className="border-b border-red-800/50 bg-red-500/10 p-3" data-testid="lang-change-alert">
                <div className="text-sm font-semibold text-red-300">⚠️ {tr("td.lang_alert", "Votre client vient de changer de langue")} ({LANG_META[plcLang]?.flag} {metaLabel(LANG_META[plcLang], tr)}).</div>
                <div className="mt-0.5 text-xs text-red-200/80">{tr("td.lang_desc_pre", "Confirmez s'il s'agit d'une erreur, ou transférez la conversation vers un agent")} {metaLabel(LANG_META[plcLang], tr)}. {tr("td.lang_desc_post", "Le nouveau message est en attente.")}</div>
                {!langPanel ? (
                  <div className="mt-2 flex gap-2">
                    <button onClick={dismissLang} disabled={busy} data-testid="lang-dismiss-btn" className="rounded-lg border border-slate-600 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-800">{tr("td.its_error", "C'est une erreur")}</button>
                    <button onClick={() => setLangPanel(true)} data-testid="lang-confirm-open" className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-500">{tr("td.confirm_change", "Confirmer le changement")}</button>
                  </div>
                ) : (
                  <div className="mt-2 space-y-2">
                    <select value={langXfer.target} onChange={(e) => setLangXfer({ ...langXfer, target: e.target.value })} data-testid="lang-target" className={inp}>
                      <option value="admin">{tr("td.administration", "Administration")}</option>
                      {newLangAgents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                    </select>
                    <input value={langXfer.reason} onChange={(e) => setLangXfer({ ...langXfer, reason: e.target.value })} placeholder={tr("td.reason_ph", "Motif (obligatoire)")} data-testid="lang-reason" className={inp} />
                    <div className="flex gap-2">
                      <button onClick={() => setLangPanel(false)} className="rounded-lg border border-slate-600 px-3 py-1.5 text-xs text-slate-300">{tr("td.cancel", "Annuler")}</button>
                      <button onClick={confirmLang} disabled={busy} data-testid="lang-confirm-btn" className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white">{tr("td.transfer_to", "Transférer vers")} {metaLabel(LANG_META[plcLang], tr)}</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* body */}
            <div className="flex-1 overflow-y-auto p-4">
              {tab === "conv" && (
                <div className="space-y-3">
                  <OrderCard o={t.order} tr={tr} />
                  {(t.messages || []).map((m) => (
                    m.quarantined ? (
                      <div key={m.id} className="max-w-[90%] rounded-2xl border border-dashed border-red-500/50 bg-red-500/10 px-3.5 py-2.5 text-sm text-red-100" data-testid={`quarantined-msg-${m.id}`}>
                        <div className="mb-0.5 text-[10px] font-semibold uppercase text-red-300">🔒 {tr("td.quarantine", "À vérifier — nouvelle langue")} ({(m.detected_language || "").toUpperCase()})</div>
                        {m.message}
                      </div>
                    ) : (
                      <div key={m.id} className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${m.sender_role === "customer" ? "bg-slate-800 text-slate-100" : "ml-auto bg-accent text-white"}`}>
                        <div className="mb-0.5 text-[10px] opacity-70">{m.sender_role === "customer" ? t.user_name : (m.sender_name || (m.sender_role === "admin" ? "Admin" : "Agent"))} · {new Date(m.created_at).toLocaleString("fr-FR")}</div>
                        {m.message}
                        <AttachmentList attachments={m.attachments} serveBase={`${base}/attachments`} />
                        {["agent", "admin"].includes(m.sender_role) && <MsgTicks m={m} tr={tr2} />}
                      </div>
                    )
                  ))}
                  <div ref={endRef} />
                  {staffTyping && <div className="text-xs italic text-slate-400" data-testid="staff-typing-indicator">{tr("td.client_typing", "Le client est en train d'écrire…")}</div>}
                </div>
              )}
              {tab === "notes" && (
                <div className="space-y-3" data-testid="drawer-notes">
                  <div className="rounded-lg border border-amber-700/40 bg-amber-500/10 p-2 text-[11px] text-amber-300">{tr("td.notes_hint", "🔒 Notes internes — jamais visibles par le client.")}</div>
                  {(t.internal_notes || []).map((n) => (
                    <div key={n.id} className="rounded-lg border border-slate-800 bg-slate-950/40 p-3 text-sm text-slate-200">
                      <div className="mb-1 text-[10px] uppercase text-slate-500">{n.author_name} · {new Date(n.created_at).toLocaleString("fr-FR")}</div>
                      {n.note}
                    </div>
                  ))}
                  {!(t.internal_notes || []).length && <div className="py-6 text-center text-xs text-slate-500">{tr("td.no_notes", "Aucune note interne.")}</div>}
                </div>
              )}
              {tab === "hist" && (
                <div className="space-y-2" data-testid="drawer-history">
                  {(t.transfer_history || []).map((h, i) => (
                    <div key={i} className="rounded-lg border border-slate-800 p-3 text-xs text-slate-300">
                      <div className="text-slate-200">{h.from_agent_name || tr("td.unassigned", "Non assigné")} → {h.to_agent_name || (h.to_admin ? tr("td.administration", "Administration") : "—")}</div>
                      <div className="mt-1 text-slate-500">{tr("td.by", "Par")} {h.by_name} ({h.by_role}) · {new Date(h.at).toLocaleString("fr-FR")}</div>
                      <div className="mt-1 text-amber-300">{tr("td.motif", "Motif :")} {h.reason}</div>
                    </div>
                  ))}
                  {!(t.transfer_history || []).length && <div className="py-6 text-center text-xs text-slate-500">{tr("td.no_transfer", "Aucun transfert.")}</div>}
                </div>
              )}
            </div>

            {/* footer actions */}
            <div className="border-t border-slate-800 p-3">
              {tab === "conv" && canReply && (
                plc ? (
                  <div className="mb-2 rounded-lg border border-amber-700/40 bg-amber-500/10 p-2 text-center text-xs text-amber-300" data-testid="reply-blocked">
                    {tr("td.reply_blocked", "Réponse bloquée : validez d'abord le changement de langue ci-dessus.")}
                  </div>
                ) : (
                  <>
                    {macros.length > 0 && (
                      <div className="relative mb-2" data-testid="macros-bar">
                        <button type="button" onClick={() => setShowMacros((v) => !v)} data-testid="macros-toggle" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800">
                          <MessageSquare className="h-3.5 w-3.5" /> {tr("td.macros", "Réponses préenregistrées")}
                        </button>
                        {showMacros && (
                          <div className="absolute bottom-full z-10 mb-1 max-h-64 w-full overflow-y-auto rounded-lg border border-slate-700 bg-slate-900 p-1 shadow-xl" data-testid="macros-menu">
                            {macros.map((mc) => (
                              <button key={mc.id} onClick={() => applyMacro(mc.id)} data-testid={`macro-${mc.id}`} className="block w-full truncate rounded px-2 py-1.5 text-left text-xs text-slate-200 hover:bg-slate-800">
                                <span className="font-medium">{mc.title}</span> <span className="text-slate-500">· {mc.category}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mb-2 flex gap-2">
                      <input value={msg} onChange={(e) => { setMsg(e.target.value); onType(); }} onKeyDown={(e) => e.key === "Enter" && reply()} placeholder={tr("td.reply_ph", "Répondre au client…")} data-testid="drawer-reply-input" className={inp} />
                      <button onClick={reply} disabled={busy} data-testid="drawer-reply-send" className="flex h-10 w-11 shrink-0 items-center justify-center rounded-lg bg-accent text-white disabled:opacity-50"><Send className="h-4 w-4" /></button>
                    </div>
                    <div className="mb-2"><AttachmentPicker files={files} setFiles={setFiles} disabled={busy} idPrefix="drawer-att" /></div>
                  </>
                )
              )}
              {tab === "notes" && (
                <div className="mb-2 flex gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder={tr("td.note_ph", "Ajouter une note interne…")} data-testid="drawer-note-input" className={inp} />
                  <button onClick={addNote} disabled={busy} data-testid="drawer-note-send" className="flex h-10 w-11 shrink-0 items-center justify-center rounded-lg bg-amber-600 text-white disabled:opacity-50"><StickyNote className="h-4 w-4" /></button>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                {canManage && ["waiting", "in_progress", "waiting_client", "waiting_internal", "resolved", "closed"].map((s) => (
                  <button key={s} onClick={() => setStatus(s)} disabled={busy || t.status === s} data-testid={`drawer-status-${s}`}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium ${t.status === s ? STATUS_META[s].color : "border border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
                    {metaLabel(STATUS_META[s], tr)}
                  </button>
                ))}
                {canTransfer && (
                  <button onClick={() => setShowTransfer((v) => !v)} data-testid="drawer-transfer-btn" className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800">
                    <ArrowRightLeft className="h-3.5 w-3.5" /> {tr("td.transfer", "Transférer")}
                  </button>
                )}
              </div>
              {showTransfer && (
                <div className="mt-3 space-y-2 rounded-lg border border-slate-800 bg-slate-950/40 p-3" data-testid="transfer-panel">
                  <select value={xfer.target} onChange={(e) => setXfer({ ...xfer, target: e.target.value })} data-testid="transfer-target" className={inp}>
                    <option value="admin">{tr("td.administration", "Administration")}</option>
                    {langAgents.map((a) => <option key={a.id} value={a.id}>{a.name}{a.allowed_languages ? ` (${a.allowed_languages.join(",")})` : ""}</option>)}
                  </select>
                  <input value={xfer.reason} onChange={(e) => setXfer({ ...xfer, reason: e.target.value })} placeholder={tr("td.transfer_reason_ph", "Motif du transfert (obligatoire)")} data-testid="transfer-reason" className={inp} />
                  <button onClick={doTransfer} disabled={busy} data-testid="transfer-confirm" className="w-full rounded-lg bg-accent py-2 text-sm font-medium text-white disabled:opacity-50">{tr("td.confirm_transfer", "Confirmer le transfert")}</button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
